/**
 * useVisitorExitAutoRevoke
 *
 * Redundant three-layer visitor state machine:
 *
 * Layer 1 — REAL-TIME SSE (person_id match):
 *   entrada → awaiting_entry visitor → auto check-in
 *   saida   → inside visitor         → auto check-out + device revoke
 *
 * Layer 2 — REAL-TIME SSE (employee_no md5 reverse-map):
 *   Same as layer 1 but uses md5(personId) lookup when person_id is missing
 *   (ControlID events often have null person_id but always have employee_no).
 *
 * Layer 3 — SCHEDULED SCAN (debounced 500ms after unresolved SSE event):
 *   CHECK-IN ONLY. Queries access history for each awaiting visitor in parallel to
 *   catch ControlID numeric IDs that can't be resolved by backend. Never checks
 *   anyone out — "dar baixa" must only ever come from a live, real-time SSE event
 *   whose direction is genuinely 'saida' right now (Layer 1/2). A scan match is a
 *   historical access-log row, not proof someone is exiting at this instant, so
 *   scan-triggered checkout was removed — it could close out a visitor based on a
 *   stale/unrelated record instead of an actual exit happening now.
 *
 * Background timer — REDUNDANCY (initial 4s delay, then every 5min):
 *   Runs same check-in-only scan regardless of SSE — catches missed entrada events
 *   and app-closed entries. Does not perform checkout, same reasoning as Layer 3.
 *
 * Retry timer — PERSISTENCE (every 60s):
 *   Retries failed entry AND exit device revokes persisted to localStorage
 *   (revokeEntryDevices/revokeExitDevices persist on failure; this timer drains
 *   both queues via retryPendingDeviceRevokes).
 *
 * Mounted in Layout — runs across all views.
 */

import { useEffect, useCallback, useRef } from 'react';
import md5 from 'md5';
import { condoApi, getApiBaseUrl, fetchWithAuth } from '../services/api';
import { AccessEvent } from './useEventStream';
import { logger } from '../services/logger';
import { fetchPersonDevices, revokeEntryDevices, revokeExitDevices, retryPendingDeviceRevokes } from '../utils/deviceRevoke';
import { useConnectionStatusOptional } from '../contexts/ConnectionStatusContext';
import { resolveAccessEventAction, VisitorRecord } from '../domain/visitorExitDetection';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AwaitingVisitor {
  personId: string;
  roleId: string;
  name: string;
  roleCreatedAt: string; // scan access history after this time
}

interface InsideVisitor {
  personId: string;
  roleId: string;
  name: string;
  entryTime: string;
}

// ─── Data fetchers ────────────────────────────────────────────────────────────

async function fetchVisitorsByState(customerId: string): Promise<{
  awaiting: AwaitingVisitor[];
  inside: InsideVisitor[];
} | null> {
  try {
    const [visitorRes, spRes] = await Promise.all([
      condoApi.getPeople(customerId, { role_type: 'visitor',          include_inactive_roles: false, page_size: 1000 }),
      condoApi.getPeople(customerId, { role_type: 'service_provider', include_inactive_roles: false, page_size: 1000 }),
    ]);

    const people = [
      ...(visitorRes.data?.data || visitorRes.data || []),
      ...(spRes.data?.data    || spRes.data    || []),
    ];

    const awaiting: AwaitingVisitor[] = [];
    const inside:   InsideVisitor[]   = [];

    for (const person of people) {
      const role = (person.roles || [])
        .filter((r: any) => (r.role_type === 'visitor' || r.role_type === 'service_provider') && r.is_active)
        .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];

      if (!role) continue;
      const name  = `${person.first_name || ''} ${person.last_name || ''}`.trim();
      const state = role.role_metadata?.visitor_state;

      if (state === 'awaiting_entry') {
        awaiting.push({ personId: person.id, roleId: role.id, name, roleCreatedAt: role.created_at || role.valid_from || new Date().toISOString() });
      } else if (state === 'inside') {
        inside.push({ personId: person.id, roleId: role.id, name, entryTime: role.role_metadata?.last_entry_at || role.role_metadata?.first_entry_at || role.created_at || role.valid_from || new Date().toISOString() });
      }
    }
    return { awaiting, inside };
  } catch (err: any) {
    // Keep last-known state on a transient fetch failure — the caller (refreshVisitors)
    // must not wipe awaitingRef/insideRef to empty just because one poll failed, or
    // exit-revoke would silently stop working for everyone currently inside until
    // the next successful poll.
    logger.error('visitor.auto', 'fetchVisitorsByState failed — keeping last-known state', err);
    return null;
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useVisitorExitAutoRevoke(customerId: string | undefined) {
  const awaitingRef     = useRef<Map<string, AwaitingVisitor>>(new Map());
  const insideRef       = useRef<Map<string, InsideVisitor>>(new Map());
  /** Maps md5(personId) → personId for SSE layer-2 reverse lookup */
  const employeeNoRef   = useRef<Map<string, string>>(new Map());
  /** Prevents double-processing the same person from concurrent SSE + background-scan */
  const inFlightRef     = useRef<Set<string>>(new Set());
  const scanRunningRef      = useRef(false);
  const pendingScanRef      = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scanWsRef           = useRef<WebSocket | null>(null);
  const scanPendingRef      = useRef<Map<number, (r: any) => void>>(new Map());
  const scanIdRef           = useRef(1);
  /** Tracks person IDs for which we've scheduled a delayed re-check after an unmatched SSE entry,
   *  mapped to the timer handle so it can be cleared on unmount. */
  const pendingEntryChecksRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  /** Whether the customer has face removal delay enabled — fetched on mount */
  const faceRemovalDelayRef = useRef(false);
  const enabled = !!customerId;

  // ── Fetch customer face removal delay setting ────────────────────────────────
  useEffect(() => {
    if (!customerId) return;
    fetchWithAuth(`${getApiBaseUrl()}/api/v1/customers/${customerId}/settings`)
      .then(r => r.ok ? r.json() : null)
      .then((data: any) => {
        if (data?.visitor_settings?.face_removal_delay_enabled != null) {
          faceRemovalDelayRef.current = !!data.visitor_settings.face_removal_delay_enabled;
        }
      })
      .catch(() => {});
  }, [customerId]);

  // ── WebSocket for batch events.visitor_scan + real-time visitor state push ───
  //
  // The WS hub pushes "visitors.updated" notifications every 5 seconds when state
  // changes (same DB poll that drives the OperationalView). We subscribe here so
  // awaitingRef / insideRef stay current in real-time, eliminating the 2-min REST
  // poll as the primary source of truth and removing the need for getPersonRoles()
  // REST fallbacks when an SSE entry event arrives for a newly-registered visitor.
  useEffect(() => {
    if (!customerId) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const wsBase = getApiBaseUrl().replace(/^http/, 'ws');
    const ws = new WebSocket(`${wsBase}/api/v1/customers/${customerId}/ws/visitors?token=${encodeURIComponent(token)}`);

    ws.onopen = () => {
      // Request large page so allVisitors contains full VisitorData (with roleCreatedAt)
      ws.send(JSON.stringify({
        jsonrpc: '2.0',
        method: 'visitors.subscribe',
        params: { page: 1, pageSize: 1000 },
      }));
    };

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);

        // ── Scan response (has numeric id) ────────────────────────────────────
        if (msg.id != null && scanPendingRef.current.has(msg.id)) {
          scanPendingRef.current.get(msg.id)!(msg.result ?? null);
          scanPendingRef.current.delete(msg.id);
          return;
        }

        // ── Hub push: rebuild awaitingRef/insideRef without REST ──────────────
        if (msg.method === 'visitors.updated' && msg.params) {
          const visitors: any[] = msg.params.visitors || [];
          const summary: any[]  = msg.params.allSummary || [];

          const newAwaiting = new Map<string, AwaitingVisitor>();
          const newInside   = new Map<string, InsideVisitor>();
          const processedIds = new Set<string>();

          // Full VisitorData from current page (has role_created_at, name)
          for (const v of visitors) {
            if (!v.personId) continue;
            processedIds.add(v.personId);
            if (v.status === 'awaiting') {
              newAwaiting.set(v.personId, {
                personId:      v.personId,
                roleId:        v.roleId,
                name:          v.name || '',
                roleCreatedAt: v.role_created_at || new Date().toISOString(),
              });
            } else if (v.status === 'inside') {
              newInside.set(v.personId, {
                personId:  v.personId,
                roleId:    v.roleId,
                name:      v.name || '',
                entryTime: v.last_entry_at || v.first_entry_at || v.role_created_at || new Date().toISOString(),
              });
            }
          }

          // VisitorSummary fallback for visitors outside the page (>1000 edge case)
          const todayStart = new Date();
          todayStart.setHours(0, 0, 0, 0);
          for (const s of summary) {
            if (!s.personId || processedIds.has(s.personId)) continue;
            if (s.status === 'awaiting') {
              newAwaiting.set(s.personId, {
                personId:      s.personId,
                roleId:        s.roleId,
                name:          '',
                roleCreatedAt: todayStart.toISOString(),
              });
            } else if (s.status === 'inside') {
              newInside.set(s.personId, {
                personId:  s.personId,
                roleId:    s.roleId,
                name:      '',
                entryTime: s.last_entry_at || s.first_entry_at || todayStart.toISOString(),
              });
            }
          }

          awaitingRef.current = newAwaiting;
          insideRef.current   = newInside;
          const allIds = Array.from(newAwaiting.keys()).concat(Array.from(newInside.keys()));
          employeeNoRef.current = new Map(allIds.map(id => [md5(id), id]));
        }
      } catch {}
    };

    ws.onerror = () => logger.warn('visitor.scan.ws', 'Scan WS error');
    scanWsRef.current = ws;
    return () => { ws.close(); scanWsRef.current = null; };
  }, [customerId]);

  const batchScanViaWS = useCallback(async (
    persons: Array<{ personId: string; since: string; direction: 'entrada' | 'saida' }>
  ): Promise<Map<string, boolean>> => {
    const ws = scanWsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return new Map();
    const id = scanIdRef.current++;
    return new Promise((resolve) => {
      scanPendingRef.current.set(id, (result: any[]) => {
        const map = new Map<string, boolean>();
        (result || []).forEach((r: any) => map.set(r.person_id, !!r.matched));
        resolve(map);
      });
      ws.send(JSON.stringify({
        jsonrpc: '2.0', id,
        method: 'events.visitor_scan',
        params: { persons: persons.map(p => ({ person_id: p.personId, since: p.since, direction: p.direction })) },
      }));
      // Resolve with empty map if no reply within 8s (WS not ready, backend slow, etc.)
      setTimeout(() => {
        if (scanPendingRef.current.has(id)) {
          scanPendingRef.current.delete(id);
          resolve(new Map());
        }
      }, 8_000);
    });
  }, []);

  const refreshVisitors = useCallback(async () => {
    if (!customerId) return;
    const result = await fetchVisitorsByState(customerId);
    if (!result) return; // fetch failed — keep existing awaitingRef/insideRef untouched
    const { awaiting, inside } = result;
    awaitingRef.current = new Map(awaiting.map(v => [v.personId, v]));
    insideRef.current   = new Map(inside.map(v => [v.personId, v]));
    // Rebuild md5 reverse-map for SSE layer-2 matching
    const allIds = [...awaiting.map(v => v.personId), ...inside.map(v => v.personId)];
    employeeNoRef.current = new Map(allIds.map(id => [md5(id), id]));
  }, [customerId]);

  useEffect(() => {
    if (!enabled) return;
    refreshVisitors();
    const id = setInterval(refreshVisitors, 10 * 60 * 1000); // WS is primary; REST is fallback
    return () => clearInterval(id);
  }, [enabled, refreshVisitors]);

  // ── Auto check-in + revoke entry devices ─────────────────────────────────────
  const checkInVisitor = useCallback(async (visitor: AwaitingVisitor, source: string) => {
    if (!customerId) return;
    if (inFlightRef.current.has(visitor.personId)) return;
    if (!awaitingRef.current.has(visitor.personId)) return;

    inFlightRef.current.add(visitor.personId);
    awaitingRef.current.delete(visitor.personId);

    try {
      logger.info('visitor.entry.auto', `[${source}] Auto check-in`, { name: visitor.name, personId: visitor.personId });

      // Fetch devices BEFORE check-in so we still have zone access
      const devices = await fetchPersonDevices(customerId, visitor.personId);

      const isElectron = typeof window !== 'undefined' && !!window.electronAPI;

      // In Electron: perform the entry-device removal at detection time and AWAIT
      // its real outcome — local_removal_done must reflect what actually happened,
      // never a fire-and-forget assumption. One fast attempt; the backend pendência
      // queue retries anything that fails.
      let entryRemovalDone = false;
      if (isElectron) {
        try {
          entryRemovalDone = await revokeEntryDevices(customerId, visitor.personId, visitor.name, devices, 1);
        } catch (err) {
          logger.error('visitor.entry.auto', 'Entry device revoke failed', err);
        }
      }

      await condoApi.autoCheckIn(customerId, visitor.personId, visitor.roleId, entryRemovalDone);
      if (isElectron && !entryRemovalDone && typeof window !== 'undefined') {
        window.dispatchEvent(new Event('panther:deferred-removals-poll'));
      }
      // Optimistically move visitor into insideRef immediately so exit detection
      // can fire right away — before the async refreshVisitors() returns.
      const entryTime = new Date().toISOString();
      insideRef.current.set(visitor.personId, { personId: visitor.personId, roleId: visitor.roleId, name: visitor.name, entryTime });
      employeeNoRef.current.set(md5(visitor.personId), visitor.personId);
      refreshVisitors(); // will overwrite with accurate server data

      // Non-Electron: nothing to do locally — the backend queued pendências that
      // the on-site Electron client will execute (the cloud never contacts devices).
    } catch (err: any) {
      logger.error('visitor.entry.auto', `[${source}] Check-in failed`, err);
      awaitingRef.current.set(visitor.personId, visitor); // re-add for retry
    } finally {
      inFlightRef.current.delete(visitor.personId);
    }
  }, [customerId, refreshVisitors]);

  // ── Auto check-out + device revoke ───────────────────────────────────────────
  const checkOutVisitor = useCallback(async (visitor: InsideVisitor, source: string) => {
    if (!customerId) return;
    if (inFlightRef.current.has(visitor.personId)) return;
    if (!insideRef.current.has(visitor.personId)) return;

    inFlightRef.current.add(visitor.personId);
    insideRef.current.delete(visitor.personId);

    try {
      logger.info('visitor.exit.auto', `[${source}] Auto check-out`, { name: visitor.name, personId: visitor.personId });

      // Fetch devices BEFORE checkout — zones empty after backend revokes access
      const devices = await fetchPersonDevices(customerId, visitor.personId);

      const isElectron = typeof window !== 'undefined' && !!window.electronAPI;
      const delayEnabled = faceRemovalDelayRef.current;

      // In Electron: perform the local removal IMMEDIATELY at exit detection and
      // AWAIT its real outcome — local_removal_done must never be claimed before
      // the removal actually succeeded (that used to skip the backend pendência
      // with the removal still unconfirmed). One fast attempt here; the pendência
      // queue is the retry net for anything that fails.
      // When face removal delay is enabled, skip immediate removal — the backend
      // enqueues a deferred removal that useDeferredDeviceRemovals executes after
      // the delay window.
      let localRemovalDone = false;
      if (isElectron && !delayEnabled) {
        try {
          localRemovalDone = await revokeExitDevices(customerId, visitor.personId, visitor.name, devices, 1);
        } catch (err) {
          logger.error('visitor.exit.auto', 'Device removal failed', err);
        }
      }

      // local_removal_done=true only when the local removal ACTUALLY completed.
      // Otherwise the backend enqueues pendências for the queue drainer.
      await condoApi.autoCheckOut(customerId, visitor.personId, visitor.roleId, localRemovalDone);
      refreshVisitors();

      // Anything not removed locally is now queued server-side — drain the queue
      // immediately instead of waiting for the next 30s poll.
      if (!localRemovalDone && typeof window !== 'undefined') {
        window.dispatchEvent(new Event('panther:deferred-removals-poll'));
      }
    } catch (err: any) {
      if (err?.response?.data?.code === 'PERSON_VALIDATION_002') return; // already checked out
      logger.error('visitor.exit.auto', `[${source}] Check-out failed`, err);
      insideRef.current.set(visitor.personId, visitor); // re-add for retry
    } finally {
      inFlightRef.current.delete(visitor.personId);
    }
  }, [customerId, refreshVisitors]);

  // ── Scan logic (shared by background timer and SSE fallback) ─────────────────
  const runScan = useCallback(async () => {
    if (scanRunningRef.current) return;
    scanRunningRef.current = true;
    try {
      const awaiting = Array.from(awaitingRef.current.values());
      const inside   = Array.from(insideRef.current.values());
      if (awaiting.length === 0 && inside.length === 0) return;

      logger.info('visitor.auto', 'Scan triggered', { awaiting: awaiting.length, inside: inside.length });

      // Single batch WS request instead of N individual REST calls.
      // Check-in only. Checkout ("dar baixa") must NEVER come from this scan — it
      // matches against historical access-log rows, not a live exit happening right
      // now, and a stale/unrelated 'saida' record from earlier can incorrectly close
      // out a visitor who is still inside. The only thing allowed to check someone
      // out is a real-time SSE event whose direction is genuinely 'saida' at this
      // exact moment (see handleEvent below) — scan-based checkout is scope-creep
      // that must be avoided entirely.
      const batchPersons = [
        ...awaiting.map(v => ({ personId: v.personId, since: v.roleCreatedAt, direction: 'entrada' as const })),
      ];
      if (batchPersons.length === 0) return;
      const results = await batchScanViaWS(batchPersons);

      await Promise.all(
        awaiting.map(async (v) => {
          if (results.get(v.personId)) await checkInVisitor(v, 'scan');
        }),
      );
    } finally {
      scanRunningRef.current = false;
    }
  }, [batchScanViaWS, checkInVisitor, checkOutVisitor]);

  // ── Debounced scan trigger — fired by unresolved SSE events ──────────────────
  const scheduleScan = useCallback(() => {
    if (pendingScanRef.current) return; // already queued
    pendingScanRef.current = setTimeout(() => {
      pendingScanRef.current = null;
      runScan();
    }, 500);
  }, [runScan]);

  // ── Layer 1+2+3: SSE real-time handler (called by Layout's shared SSE connection) ──
  //
  // We deliberately do NOT open our own useEventStream here.
  // Layout.tsx already has a working SSE connection; sharing it guarantees
  // this handler fires on the exact same events that show the top-right notification.
  // A second independent connection risks being silently dropped by the backend.
  const handleEvent = useCallback((event: AccessEvent) => {
    if (!event.is_authorized) return;

    // AwaitingVisitor structurally satisfies VisitorRecord (has all required fields).
    // InsideVisitor needs roleCreatedAt added (we reuse entryTime as a sentinel value —
    // only personId is accessed on the returned visitor by the callers below).
    const awaitingVR = awaitingRef.current as ReadonlyMap<string, VisitorRecord>;
    const insideVR: ReadonlyMap<string, VisitorRecord> = new Map(
      Array.from(insideRef.current.entries()).map(([k, v]) => [k, { ...v, roleCreatedAt: v.entryTime }])
    );

    const action = resolveAccessEventAction(
      {
        personId:     event.person_id,
        employeeNo:   event.employee_no,
        direction:    event.direction,
        isAuthorized: true, // guard is at the top of this function
      },
      awaitingVR,
      insideVR,
      employeeNoRef.current,
    );

    switch (action.kind) {
      case 'check_in': {
        const v = awaitingRef.current.get(action.visitor.personId);
        if (v) checkInVisitor(v, action.source);
        return;
      }
      case 'check_out': {
        const v = insideRef.current.get(action.visitor.personId);
        if (v) checkOutVisitor(v, action.source);
        return;
      }
      case 'scan':
        scheduleScan();
        break;
      case 'none':
        break;
    }

    // Unrecognized entry (scan / none): awaitingRef may be momentarily stale —
    // the WS hub pushes visitor state every 5s. Schedule a delayed re-check so that
    // when the push arrives and awaitingRef is updated, the visitor gets checked in.
    const dir = event.direction?.toLowerCase();
    if (event.person_id &&
        (!dir || dir === 'entrada' || dir === 'passagem') &&
        !inFlightRef.current.has(event.person_id) &&
        !pendingEntryChecksRef.current.has(event.person_id)) {
      const pid = event.person_id;
      const timer = setTimeout(() => {
        pendingEntryChecksRef.current.delete(pid);
        const v = awaitingRef.current.get(pid);
        if (v && !inFlightRef.current.has(pid)) {
          checkInVisitor(v, 'SSE-ws-delayed');
        }
      }, 7_000);
      pendingEntryChecksRef.current.set(pid, timer);
    }
  }, [checkInVisitor, checkOutVisitor, scheduleScan]);

  // Clear any still-pending delayed re-check timers on unmount (e.g. logout/customer switch)
  // so they don't fire against stale refs after the hook is gone.
  useEffect(() => () => {
    Array.from(pendingEntryChecksRef.current.values()).forEach(timer => clearTimeout(timer));
    pendingEntryChecksRef.current.clear();
  }, []);

  // ── Background scan — pure redundancy, 5min interval ────────────────────────
  useEffect(() => {
    if (!enabled) return;
    const initialTimer = setTimeout(runScan, 4000);          // initial scan after data loads
    const intervalId   = setInterval(runScan, 5 * 60 * 1000); // repeat every 5min (SSE handles real-time)
    return () => { clearTimeout(initialTimer); clearInterval(intervalId); };
  }, [enabled, runScan]);

  // ── SSE reconnection recovery — scan at 30s for 3 cycles, then back to 5min ─
  const recoveryTimerRef  = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevConnectedRef  = useRef<boolean | null>(null);
  const connectionStatus  = useConnectionStatusOptional();

  useEffect(() => {
    if (!enabled) return;
    const isNowConnected = connectionStatus?.status.isConnected ?? null;
    if (isNowConnected === null) return;

    if (prevConnectedRef.current === false && isNowConnected === true) {
      // SSE just came back — immediate catch-up scan + 3 short-interval scans
      logger.info('visitor.auto', 'SSE reconnected — entering recovery scan mode');
      runScan();

      if (recoveryTimerRef.current) clearInterval(recoveryTimerRef.current);
      let remaining = 3;
      recoveryTimerRef.current = setInterval(() => {
        runScan();
        remaining--;
        if (remaining <= 0) {
          clearInterval(recoveryTimerRef.current!);
          recoveryTimerRef.current = null;
          logger.info('visitor.auto', 'Recovery scan mode ended — resuming 5min interval');
        }
      }, 30_000);
    }

    prevConnectedRef.current = isNowConnected;
  }, [enabled, connectionStatus?.status.isConnected, runScan]);

  useEffect(() => () => {
    if (recoveryTimerRef.current) clearInterval(recoveryTimerRef.current);
  }, []);

  // ── Retry pending entry + exit revokes every minute ───────────────────────────
  useEffect(() => {
    retryPendingDeviceRevokes(); // immediate check on mount
    const id = setInterval(retryPendingDeviceRevokes, 60 * 1000);
    return () => clearInterval(id);
  }, []);

  return { handleEvent };
}
